import type { FilterConfig } from "@atlas/core";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { HOME_APPLICATION_FILTER_ALL } from "../homeLaunchpadFilters.utils";

const HOME_LAUNCHPAD_FILTER_TABLE_ID = "home-launchpad-filters";
const APPLICATION_NAME_COLUMN_ID = "applicationName";

type HomeLaunchpadFilterRow = { id: string };

const HOME_LAUNCHPAD_FILTER_COLUMNS: ColumnDef<HomeLaunchpadFilterRow>[] = [
  { id: APPLICATION_NAME_COLUMN_ID, accessorKey: APPLICATION_NAME_COLUMN_ID },
];

export function resolveHomeLaunchpadApplicationNames(
  columnFilters: ColumnFiltersState,
  availableApplicationNames: string[],
): string[] {
  const available = new Set(availableApplicationNames);
  const applicationFilter = columnFilters.find(
    (filter) => filter.id === APPLICATION_NAME_COLUMN_ID,
  )?.value;
  const values = Array.isArray(applicationFilter) ? applicationFilter : [];

  return values.filter(
    (value): value is string =>
      typeof value === "string" && available.has(value),
  );
}

export function useHomeLaunchpadFilterTable(
  translate: (key: string, options?: { defaultValue?: string }) => string,
  availableApplicationNames: string[],
) {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const data = useMemo<HomeLaunchpadFilterRow[]>(
    () => [{ id: HOME_LAUNCHPAD_FILTER_TABLE_ID }],
    [],
  );

  const table = useReactTable({
    data,
    columns: HOME_LAUNCHPAD_FILTER_COLUMNS,
    state: { columnFilters },
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
  });

  const filters = useMemo<FilterConfig[]>(
    () => [
      {
        columnId: APPLICATION_NAME_COLUMN_ID,
        label: translate("hub.home.filter_by_application", {
          defaultValue: "Application",
        }),
        options: availableApplicationNames.map((applicationName) => ({
          value: applicationName,
          label: applicationName,
        })),
      },
    ],
    [availableApplicationNames, translate],
  );

  const selectedApplicationNames = useMemo(
    () =>
      resolveHomeLaunchpadApplicationNames(
        columnFilters,
        availableApplicationNames,
      ),
    [availableApplicationNames, columnFilters],
  );

  const activeFilterCount = useMemo(
    () => selectedApplicationNames.length,
    [selectedApplicationNames],
  );

  const setApplicationNames = useCallback((applicationNames: string[]) => {
    setColumnFilters(
      applicationNames.length > 0
        ? [{ id: APPLICATION_NAME_COLUMN_ID, value: applicationNames }]
        : [],
    );
  }, []);

  const selectApplicationFilter = useCallback(
    (applicationName: string) => {
      if (applicationName === HOME_APPLICATION_FILTER_ALL) {
        setApplicationNames([]);
        return;
      }

      setApplicationNames([applicationName]);
    },
    [setApplicationNames],
  );

  return {
    table,
    filters,
    selectedApplicationNames,
    activeFilterCount,
    selectApplicationFilter,
  };
}
